import { useEffect, useState } from "react";
import { createFileRoute, Outlet, useNavigate, useLocation } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";

export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
});

type AuthState = "checking" | "authenticated" | "unauthenticated" | "banned";

async function checkBanned(userId: string): Promise<boolean> {
  const { data } = await supabase
    .from("profiles")
    .select("banned")
    .eq("user_id", userId)
    .maybeSingle();
  return Boolean(data?.banned);
}

function AuthenticatedLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState<AuthState>("checking");

  useEffect(() => {
    let mounted = true;

    const handleSession = async (session: { user: { id: string } } | null) => {
      if (!mounted) return;
      if (!session) {
        setState("unauthenticated");
        return;
      }
      const banned = await checkBanned(session.user.id);
      if (!mounted) return;
      if (banned) {
        await supabase.auth.signOut();
        if (!mounted) return;
        setState("banned");
        return;
      }
      setState("authenticated");
    };

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      void handleSession(session ?? null);
    });

    supabase.auth.getSession().then(({ data }) => {
      void handleSession(data.session ?? null);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (state === "unauthenticated") {
      const path = location.pathname;
      const redirect = path.startsWith("/login") || path.startsWith("/signup") ? undefined : path;
      navigate({
        to: "/login",
        search: { redirect } as never,
        replace: true,
      });
    } else if (state === "banned") {
      toast.error("このアカウントは停止されています。お問い合わせください。");
      navigate({
        to: "/login",
        search: { banned: "1" } as never,
        replace: true,
      });
    }
  }, [state, location.href, navigate]);

  if (state === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm">認証情報を確認しています...</p>
        </div>
      </div>
    );
  }

  if (state === "unauthenticated" || state === "banned") {
    // Redirect is in flight; render nothing to avoid flashing protected content.
    return null;
  }

  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
}
